import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Activity, Target, Layers, Sparkles, Sun, CloudRain, Wind } from 'lucide-react';
import HistoricalTrendChart from '../components/HistoricalTrendChart';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Lightweight markdown-lite renderer — handles headings, bold, and paragraphs. */
const renderVerdict = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { out.push(<div key={i} className="h-2" />); return; }
    if (line.startsWith('### ')) {
      out.push(<h4 key={i} className="text-white font-semibold text-sm mt-3">{line.slice(4)}</h4>);
    } else if (line.startsWith('## ')) {
      out.push(<h3 key={i} className="text-white font-bold text-base mt-3">{line.slice(3)}</h3>);
    } else if (line.startsWith('# ')) {
      out.push(<h2 key={i} className="text-white font-bold text-lg mt-3">{line.slice(2)}</h2>);
    } else {
      // inline **bold** rendering
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      out.push(
        <p key={i} className="text-gray-300 text-sm leading-relaxed">
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j} className="text-golden">{part.slice(2, -2)}</strong>
              : <span key={j}>{part}</span>
          )}
        </p>
      );
    }
  });
  return out;
};

const fmt = (n, dp = 2) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : Number(n).toFixed(dp));
const pct = (n, dp = 2) => (n === null || n === undefined ? '—' : `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(dp)}%`);
const priceColor = (n) => (n === null || n === undefined ? 'text-gray-400' : Number(n) >= 0 ? 'text-emerald-400' : 'text-red-400');

const MoodIcon = ({ mood }) => {
  const m = (mood || '').toLowerCase();
  if (m.includes('bull')) return <Sun className="w-5 h-5 text-emerald-400" />;
  if (m.includes('bear')) return <CloudRain className="w-5 h-5 text-red-400" />;
  return <Wind className="w-5 h-5 text-amber-400" />;
};

const StatPill = ({ label, value, sub, tone = 'default' }) => {
  const toneMap = {
    default: 'text-white',
    positive: 'text-emerald-400',
    negative: 'text-red-400',
    warning: 'text-amber-400',
  };
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-3">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${toneMap[tone]}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const CheckMA = ({ label, ma, pos }) => (
  <div className="flex items-center justify-between text-xs py-1">
    <span className="text-gray-500">{label}</span>
    <span className="flex items-center gap-1">
      <span className="font-mono text-gray-300">{fmt(ma, 1)}</span>
      {pos === 'above' && <span className="text-emerald-400">✅ above</span>}
      {pos === 'below' && <span className="text-red-400">❌ below</span>}
      {pos === 'n/a' && <span className="text-gray-500">—</span>}
    </span>
  </div>
);

const ForecastCard = ({ data }) => {
  if (!data || !data.available) return null;
  const p = data.pivots || {};
  return (
    <div className="bg-gradient-to-b from-white/[0.03] to-black/40 border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-golden" />
        <h4 className="text-white font-semibold text-sm">{data.name} &mdash; Tomorrow&apos;s Bias</h4>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <StatPill label="Close" value={fmt(data.close, 2)} />
        <StatPill
          label="vs PDH / PDL"
          value={`${data.vs_pdh === 'above' ? '↑' : '↓'} PDH · ${data.vs_pdl === 'above' ? '↑' : '↓'} PDL`}
          sub={`PDH ${fmt(data.prev_high, 1)} · PDL ${fmt(data.prev_low, 1)}`}
        />
      </div>

      <div className="mt-2">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Moving Averages</p>
        <div className="bg-black/30 rounded-md px-3 py-1 divide-y divide-white/5">
          <CheckMA label="20-DMA" ma={data.ma20} pos={data.ma20_pos} />
          <CheckMA label="50-DMA" ma={data.ma50} pos={data.ma50_pos} />
          <CheckMA label="200-DMA" ma={data.ma200} pos={data.ma200_pos} />
        </div>
      </div>

      <div className="mt-3">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Pivots</p>
        <div className="grid grid-cols-5 gap-1 text-[11px]">
          <div className="bg-red-500/10 border border-red-500/20 rounded p-1.5 text-center">
            <p className="text-red-300 font-mono">{fmt(p.s2, 0)}</p>
            <p className="text-gray-500 text-[9px]">S2</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/20 rounded p-1.5 text-center">
            <p className="text-red-300 font-mono">{fmt(p.s1, 0)}</p>
            <p className="text-gray-500 text-[9px]">S1</p>
          </div>
          <div className="bg-golden/10 border border-golden/30 rounded p-1.5 text-center">
            <p className="text-golden font-mono font-bold">{fmt(p.pivot, 0)}</p>
            <p className="text-gray-500 text-[9px]">Pivot</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-1.5 text-center">
            <p className="text-emerald-300 font-mono">{fmt(p.r1, 0)}</p>
            <p className="text-gray-500 text-[9px]">R1</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-1.5 text-center">
            <p className="text-emerald-300 font-mono">{fmt(p.r2, 0)}</p>
            <p className="text-gray-500 text-[9px]">R2</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Semicircular sentiment gauge — score 0..100 with a Bullish/Neutral/Bearish label. */
const SentimentGauge = ({ score, mood }) => {
  const s = Math.max(0, Math.min(100, Number(score ?? 50)));
  const angle = -90 + (s / 100) * 180; // -90 (left) .. +90 (right)
  const color = s >= 60 ? '#10b981' : s <= 40 ? '#ef4444' : '#f59e0b';
  return (
    <div className="flex flex-col items-center justify-center py-2" data-testid="sentiment-gauge">
      <div className="relative w-40 h-20 overflow-hidden">
        <div className="absolute inset-0 rounded-t-full"
          style={{ background: 'conic-gradient(from 270deg at 50% 100%, #ef4444 0deg, #f59e0b 90deg, #10b981 180deg)' }} />
        <div className="absolute inset-[10px] rounded-t-full bg-[#0b0b0d]" />
        <div className="absolute left-1/2 bottom-0 w-[3px] h-[68px] origin-bottom rounded"
          style={{ background: color, transform: `translateX(-50%) rotate(${angle}deg)` }} />
        <div className="absolute left-1/2 bottom-0 w-3 h-3 -translate-x-1/2 translate-y-1/2 rounded-full" style={{ background: color }} />
      </div>
      <div className="text-center -mt-1">
        <p className="text-2xl font-bold text-white leading-none">{Math.round(s)}<span className="text-sm text-gray-500">/100</span></p>
        <p className="text-sm font-semibold mt-1" style={{ color }}>{mood}</p>
      </div>
    </div>
  );
};

const biasTone = (bias) => {
  const b = (bias || '').toLowerCase();
  if (b.includes('bull')) return { tag: 'STRONG BULLISH', cls: 'text-emerald-400', chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' };
  if (b.includes('bear')) return { tag: 'STRONG BEARISH', cls: 'text-red-400', chip: 'bg-red-500/15 text-red-300 border-red-500/30' };
  return { tag: 'NEUTRAL', cls: 'text-amber-400', chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30' };
};

const TradeIdeaCard = ({ idea }) => {
  const t = biasTone(idea.bias);
  return (
    <div className="bg-gradient-to-br from-white/[0.04] to-black/50 border border-white/10 rounded-xl p-4 hover:border-golden/30 transition-colors" data-testid="trade-idea-card">
      <div className="flex items-start justify-between mb-1">
        <div>
          <p className="text-white font-bold text-sm">{idea.instrument}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{idea.strategy}</p>
        </div>
        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${t.chip}`}>{t.tag}</span>
      </div>
      <div className="grid grid-cols-3 gap-1 mt-3 text-center">
        <div><p className="text-[9px] text-gray-500 uppercase">Entry</p><p className="text-xs font-mono text-white">{fmt(idea.entry_price, 1)}</p></div>
        <div><p className="text-[9px] text-gray-500 uppercase">Target</p><p className="text-xs font-mono text-emerald-400">{fmt(idea.target_1, 1)}</p></div>
        <div><p className="text-[9px] text-gray-500 uppercase">Stop</p><p className="text-xs font-mono text-red-400">{fmt(idea.stop_loss, 1)}</p></div>
      </div>
      {idea.rationale && <p className="text-[11px] text-gray-400 mt-3 leading-snug line-clamp-2">{idea.rationale}</p>}
    </div>
  );
};


const MarketSnapshotPage = () => {
  const { date: routeDate } = useParams();
  const navigate = useNavigate();
  // IST date so it matches the backend snapshot cron (which runs 4 PM IST).
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  const [date, setDate] = useState(routeDate || today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');
  const [dates, setDates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/market-snapshot/list?days=90`);
        const j = await res.json();
        setDates((j.snapshots || []).map(s => s.date));
      } catch (_e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let pollTimer;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/market-snapshot/${date}`);
        if (res.status === 202) {
          // Snapshot is being built server-side. Poll every 15s.
          const j = await res.json().catch(() => ({}));
          if (!cancelled) {
            setBuilding(true);
            setData(null);
            setError('');
            const retryMs = ((j?.retry_after_seconds || 30) * 1000) / 2;  // check twice as often
            pollTimer = setTimeout(load, Math.max(15000, retryMs));
          }
          return;
        }
        if (!res.ok) {
          if (!cancelled) {
            setBuilding(false);
            setError(res.status === 404 ? `No snapshot for ${date}` : `Failed to load (HTTP ${res.status})`);
            setData(null);
          }
        } else {
          const j = await res.json();
          if (!cancelled) {
            setBuilding(false);
            setData(j);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setBuilding(false);
          setError(e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [date]);

  const changeDate = (delta) => {
    const idx = dates.indexOf(date);
    if (idx < 0) return;
    const next = dates[idx - delta]; // dates sorted desc, so -1 = older, +1 = newer
    if (next) {
      setDate(next);
      navigate(`/market-snapshot/${next}`);
    }
  };

  const nifty = data?.indices?.['NIFTY 50'] || {};
  const bnf = data?.indices?.['BANK NIFTY'] || {};
  const sensex = data?.indices?.['SENSEX'] || {};
  const vix = data?.indices?.['INDIA VIX'] || {};
  const breadth = data?.breadth || {};
  const fii = data?.fii_dii || {};
  const gainers = data?.fno_gainers || [];
  const losers = data?.fno_losers || [];
  const sectoral = useMemo(() => {
    let arr = data?.sectoral || [];
    // Fallback: derive sector list from the indices map when the snapshot didn't
    // persist a dedicated sectoral array (older builds).
    if ((!arr || arr.length === 0) && data?.indices) {
      const headline = new Set(['NIFTY 50', 'BANK NIFTY', 'SENSEX', 'INDIA VIX', 'NIFTY BANK']);
      arr = Object.entries(data.indices)
        .filter(([k]) => k.toUpperCase().startsWith('NIFTY') && !headline.has(k.toUpperCase()))
        .map(([k, v]) => ({ name: k, change_percent: v?.change_percent }));
    }
    return [...arr].sort((a, b) => (b.change_percent ?? -99) - (a.change_percent ?? -99));
  }, [data]);
  const sentimentScore = data?.sentiment?.score;
  const sentimentMood = data?.sentiment?.mood || (
    sentimentScore >= 60 ? 'Bullish' : sentimentScore <= 40 ? 'Bearish' : 'Neutral'
  );
  const dedupeByInstrument = (arr) => {
    const seen = new Set();
    return (arr || []).filter((x) => {
      const key = (x.instrument || '') + '|' + (x.strategy || '');
      if (seen.has(x.instrument)) return false;
      seen.add(x.instrument);
      return true;
    });
  };
  const topIdeas = dedupeByInstrument(data?.top_ideas || []);
  const optionIdeas = dedupeByInstrument(data?.top_option_ideas || []);
  const mostActive = data?.most_active || [];
  const forecasts = [data?.nifty_forecast, data?.bnf_forecast].filter((f) => f && f.available);
  const hasFiiBreakdown = fii.fii_buy != null || fii.fii_sell != null;
  const totalBreadth = (Number(breadth.advances) || 0) + (Number(breadth.declines) || 0) + (Number(breadth.unchanged) || 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-100 py-6 px-4" data-testid="market-snapshot-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6 sticky top-16 md:top-[72px] z-10 bg-black/60 backdrop-blur-md py-2 -mx-4 px-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => changeDate(1)}
              className="p-1.5 rounded hover:bg-white/10"
              title="Newer day"
              data-testid="snapshot-newer-btn"
            ><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
              <Calendar className="w-4 h-4 text-golden" />
              <input
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); navigate(`/market-snapshot/${e.target.value}`); }}
                max={today}
                className="bg-transparent text-sm font-semibold text-white focus:outline-none"
                data-testid="snapshot-date-picker"
              />
            </div>
            <button
              onClick={() => changeDate(-1)}
              className="p-1.5 rounded hover:bg-white/10"
              title="Older day"
              data-testid="snapshot-older-btn"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
            <MoodIcon mood={sentimentMood} />
            <span className="text-sm font-semibold text-white">{sentimentMood}</span>
            {sentimentScore != null && (
              <span className="text-xs text-gray-400">· score {sentimentScore}</span>
            )}
          </div>
        </div>

        {loading && !building && <p className="py-8 text-center text-gray-500">Loading market snapshot…</p>}
        {building && (
          <div className="py-10 flex flex-col items-center justify-center text-center gap-3" data-testid="snapshot-building">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-golden animate-pulse" />
              <div className="w-3 h-3 rounded-full bg-golden animate-pulse" style={{ animationDelay: '150ms' }} />
              <div className="w-3 h-3 rounded-full bg-golden animate-pulse" style={{ animationDelay: '300ms' }} />
            </div>
            <p className="text-golden font-semibold">Generating today&apos;s snapshot…</p>
            <p className="text-xs text-gray-500 max-w-md">
              We&apos;re pulling live indices, F&amp;O universe change%, FII/DII flows and computing the AI verdict.
              This takes ~60-90 seconds after market close. The page will refresh automatically.
            </p>
          </div>
        )}
        {error && !building && <p className="py-8 text-center text-red-400">{error}</p>}

        {data && (
          <>
            {/* Section 1 — Headline Indices */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="section-indices">
              {[
                { key: 'NIFTY 50', label: 'Nifty 50', data: nifty },
                { key: 'BANK NIFTY', label: 'Bank Nifty', data: bnf },
                { key: 'SENSEX', label: 'Sensex', data: sensex },
                { key: 'INDIA VIX', label: 'India VIX', data: vix, isVix: true },
              ].map((it) => {
                const cp = it.data?.change_percent;
                return (
                  <div key={it.key} className="bg-gradient-to-br from-white/5 to-black/60 border border-white/10 rounded-xl p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">{it.label}</p>
                    <p className="text-2xl font-bold text-white mt-1">{fmt(it.data?.ltp, 2)}</p>
                    <p className={`text-sm font-semibold mt-0.5 ${priceColor(cp)}`}>
                      {cp !== undefined && cp !== null ? (cp >= 0 ? <TrendingUp className="w-3 h-3 inline mb-0.5" /> : <TrendingDown className="w-3 h-3 inline mb-0.5" />) : null}
                      {' '}{pct(cp)}
                    </p>
                    {it.data?.day_range && (
                      <p className="text-[10px] text-gray-500 mt-1">Range: {it.data.day_range}</p>
                    )}
                  </div>
                );
              })}
            </section>

            {/* Sentiment gauge + breadth summary */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="section-sentiment">
              <div className="bg-gradient-to-br from-white/5 to-black/60 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Market Sentiment</p>
                <SentimentGauge score={sentimentScore} mood={sentimentMood} />
              </div>
              <div className="md:col-span-2 bg-black/40 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-white">Market Breadth</h3>
                  {totalBreadth > 0 && <span className="text-[11px] text-gray-500">across {totalBreadth} tracked stocks</span>}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 py-4">
                    <p className="text-3xl font-bold text-emerald-400">{fmt(breadth.advances, 0)}</p>
                    <p className="text-[11px] text-emerald-300/70 uppercase mt-1">Advancing</p>
                  </div>
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 py-4">
                    <p className="text-3xl font-bold text-red-400">{fmt(breadth.declines, 0)}</p>
                    <p className="text-[11px] text-red-300/70 uppercase mt-1">Declining</p>
                  </div>
                  <div className="rounded-lg bg-white/5 border border-white/10 py-4">
                    <p className="text-3xl font-bold text-gray-300">{fmt(breadth.unchanged, 0)}</p>
                    <p className="text-[11px] text-gray-500 uppercase mt-1">Unchanged</p>
                  </div>
                </div>
                {breadth.advances != null && breadth.declines != null ? (
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    Advance / Decline Ratio:{' '}
                    <span className={`font-bold ${breadth.advances >= breadth.declines ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(breadth.advances / (breadth.declines || 1)).toFixed(2)}
                    </span>
                  </p>
                ) : null}
              </div>
            </section>

            {/* 🏆 Today's Top Trade Ideas */}
            {topIdeas.length > 0 && (
              <section className="mb-6" data-testid="section-top-ideas">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="text-base">🏆</span> Today&apos;s Top Trade Ideas
                  <span className="text-[10px] text-golden bg-golden/10 border border-golden/25 rounded-full px-2 py-0.5">RESEARCH SCANNER</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {topIdeas.map((idea, i) => <TradeIdeaCard key={i} idea={idea} />)}
                </div>
              </section>
            )}

            {/* 🏦 FII/DII Flow */}
            <section className="mb-6" data-testid="section-fii-dii">
              <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-4 h-4 text-purple-400" />
                  <h3 className="text-sm font-semibold text-white">FII / DII Flow (₹ crore)</h3>
                  <span className="text-[10px] text-gray-500">Estimated</span>
                </div>
                <div className={`grid gap-2 ${hasFiiBreakdown ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'}`}>
                  {fii.fii_buy != null && <StatPill label="FII Buy" value={`+${fmt(fii.fii_buy, 0)}`} tone="positive" />}
                  {fii.fii_sell != null && <StatPill label="FII Sell" value={`-${fmt(Math.abs(fii.fii_sell), 0)}`} tone="negative" />}
                  <StatPill label="FII Net" value={fmt(fii.fii_net, 0)} tone={fii.fii_net >= 0 ? 'positive' : 'negative'} sub={fii.fii_net >= 0 ? 'Buying' : 'Selling'} />
                  <StatPill label="DII Net" value={fmt(fii.dii_net, 0)} tone={fii.dii_net >= 0 ? 'positive' : 'negative'} sub={fii.dii_net >= 0 ? 'Buying' : 'Selling'} />
                </div>
              </div>
            </section>

            {/* Section 3 — F&O Movers */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="section-fno-movers">
              <div className="bg-emerald-500/[0.03] border border-emerald-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-emerald-300">Top F&O Gainers</h3>
                </div>
                <div className="space-y-1.5">
                  {gainers.length === 0 ? (
                    <p className="text-xs text-gray-500 py-2">No data.</p>
                  ) : gainers.map((g, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                      <span className="font-semibold text-white">{g.symbol}</span>
                      <span className="flex items-center gap-2 text-emerald-400 font-mono">
                        {fmt(g.ltp, 1)}
                        <span className="bg-emerald-500/20 px-1.5 rounded font-bold">{pct(g.change_percent)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-red-500/[0.03] border border-red-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  <h3 className="text-sm font-semibold text-red-300">Top F&O Losers</h3>
                </div>
                <div className="space-y-1.5">
                  {losers.length === 0 ? (
                    <p className="text-xs text-gray-500 py-2">No data.</p>
                  ) : losers.map((g, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                      <span className="font-semibold text-white">{g.symbol}</span>
                      <span className="flex items-center gap-2 text-red-400 font-mono">
                        {fmt(g.ltp, 1)}
                        <span className="bg-red-500/20 px-1.5 rounded font-bold">{pct(g.change_percent)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Section 4 — Sectoral Heatmap */}
            {sectoral.length > 0 && (
              <section className="mb-6" data-testid="section-sectoral">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-amber-400" /> Sectoral Performance
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {sectoral.map((s, i) => {
                    const cp = s.change_percent ?? 0;
                    const intensity = Math.min(1, Math.abs(cp) / 3);
                    const bg = cp >= 0
                      ? `rgba(16, 185, 129, ${0.15 + intensity * 0.3})`
                      : `rgba(239, 68, 68, ${0.15 + intensity * 0.3})`;
                    return (
                      <div key={i} className="rounded-lg p-2 text-center border border-white/10" style={{ backgroundColor: bg }}>
                        <p className="text-[10px] text-gray-200 uppercase truncate">{(s.name || s.symbol || '').replace('NIFTY ', '')}</p>
                        <p className={`text-sm font-bold ${cp >= 0 ? 'text-emerald-200' : 'text-red-200'}`}>{pct(cp)}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Section 4.5 — Historical Trend (Nifty close + FII net) */}
            <HistoricalTrendChart days={20} />

            {/* ⚡ Top Trade in Stock Options */}
            <section className="mb-6" data-testid="section-option-trades">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <span className="text-base">⚡</span> Top Option Trades
                <span className="text-[10px] text-gray-500">Research Scanner</span>
              </h3>
              {optionIdeas.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {optionIdeas.map((idea, i) => <TradeIdeaCard key={i} idea={idea} />)}
                </div>
              ) : (
                <div className="bg-black/30 border border-dashed border-white/10 rounded-xl p-5 text-center text-xs text-gray-500">
                  No option setups published today · Scans run during market hours
                </div>
              )}
            </section>

            {/* ⚡ Most Active by Turnover */}
            <section className="mb-6" data-testid="section-most-active">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <span className="text-base">⚡</span> Most Active by Turnover
                <span className="text-[10px] text-gray-500">Heavy institutional interest</span>
              </h3>
              {mostActive.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {mostActive.map((m, i) => (
                    <div key={i} className="flex items-center justify-between bg-black/40 border border-white/10 rounded-lg px-3 py-2">
                      <div>
                        <p className="text-white font-semibold text-sm">{m.symbol}</p>
                        <p className="text-[10px] text-gray-500">Turnover: ₹{fmt(m.turnover_cr, 0)}Cr</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono text-white">{fmt(m.ltp, 1)}</p>
                        <p className={`text-xs font-semibold ${priceColor(m.change_percent)}`}>{pct(m.change_percent)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-black/30 border border-dashed border-white/10 rounded-xl p-5 text-center text-xs text-gray-500">
                  Turnover data available when the live market feed is connected.
                </div>
              )}
            </section>


            {/* Section 5 — Next-Day Forecast */}
            {forecasts.length > 0 && (
              <section className={`grid grid-cols-1 ${forecasts.length > 1 ? 'md:grid-cols-2' : ''} gap-3 mb-6`} data-testid="section-forecast">
                {forecasts.map((f, i) => <ForecastCard key={i} data={f} />)}
              </section>
            )}

            {/* Section 6 — Verdict */}
            {data.verdict_md && (
              <section className="mb-8" data-testid="section-verdict">
                <div className="bg-gradient-to-b from-golden/[0.05] to-black/40 border border-golden/25 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-golden" />
                    <h3 className="text-sm font-semibold text-golden uppercase tracking-wider">AI Market Verdict</h3>
                  </div>
                  <div className="max-w-none">
                    {renderVerdict(data.verdict_md)}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-3 italic">
                    Educational analysis only. Past performance is not indicative of future results.
                  </p>
                </div>
              </section>
            )}

            {/* CTA — Unlock full access */}
            <section className="mb-6" data-testid="section-cta">
              <div className="bg-gradient-to-r from-golden/15 to-amber-500/10 border border-golden/30 rounded-2xl p-6 text-center">
                <h3 className="text-lg font-bold text-white">Unlock Full Research Access</h3>
                <p className="text-sm text-gray-300 mt-1 max-w-xl mx-auto">
                  Get live AI Trade Ideas, premium Telegram signals, market intelligence &amp; mentorship — all in one platform.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-4 inline-flex items-center gap-2 bg-golden hover:bg-golden/90 text-black font-semibold px-6 py-3 rounded-full transition-transform hover:scale-105"
                  data-testid="snapshot-cta-btn"
                >
                  View Premium Plans →
                </button>
              </div>
            </section>

            <p className="text-center text-[10px] text-gray-500">
              Snapshot generated at {new Date(data.generated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              {' · '}source: uppercircuitstocks.com
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketSnapshotPage;
